// Utility functions for booking data manipulation

/**
 * Detect duplicate bookings based on matching criteria:
 * - Same date, start_time, end_time, court, player_mobile, booking_source
 * - Email bookings that match existing bookings are marked as duplicates
 * Returns bookings with is_duplicate flag set
 */
export const detectDuplicates = (bookings) => {
  if (!bookings || bookings.length === 0) return [];

  // Create a map to track unique bookings
  const seenMap = new Map();
  
  // Sort bookings by created_at or id to ensure earlier entries are primary
  const sortedBookings = [...bookings].sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
    const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
    return dateA - dateB;
  });

  return sortedBookings.map((booking) => {
    // Create a unique key for duplicate detection
    // Match on: date + start_time + end_time + court + player_mobile
    const key = `${booking.date}_${booking.start_time}_${booking.end_time}_${booking.court}_${booking.player_mobile || 'no-mobile'}`;
    
    if (seenMap.has(key)) {
      const original = seenMap.get(key);
      // Mark as duplicate - email bookings matching existing bookings are duplicates
      return {
        ...booking,
        is_duplicate: true,
        duplicate_of: original.booking_id,
      };
    } else {
      seenMap.set(key, booking);
      return {
        ...booking,
        is_duplicate: false,
      };
    }
  });
};

/**
 * Filter out duplicate bookings
 */
export const filterOutDuplicates = (bookings) => {
  return bookings.filter(b => !b.is_duplicate);
};

/**
 * Merge multiple bookings from the same person on the same date into one entry
 * Groups by: player_mobile + date + venue + game + court
 * Aggregates: start_time (earliest), end_time (latest), amount (sum)
 * Excludes duplicates from merging
 */
export const mergeConsecutiveBookings = (bookings, excludeDuplicates = true) => {
  if (!bookings || bookings.length === 0) return [];

  // First detect duplicates
  const bookingsWithDuplicateFlag = detectDuplicates(bookings);
  
  // Optionally filter out duplicates before merging
  const bookingsToMerge = excludeDuplicates 
    ? bookingsWithDuplicateFlag.filter(b => !b.is_duplicate)
    : bookingsWithDuplicateFlag;

  // Group bookings by player_mobile + date + venue + game + court
  const groupedMap = new Map();

  bookingsToMerge.forEach((booking) => {
    // Create a unique key for grouping
    const key = `${booking.player_mobile || 'no-mobile'}_${booking.date}_${booking.venue}_${booking.game}_${booking.court}`;
    
    if (!groupedMap.has(key)) {
      groupedMap.set(key, {
        ...booking,
        original_bookings: [booking],
        merged_count: 1,
      });
    } else {
      const existing = groupedMap.get(key);
      
      // Compare and update start_time (keep earliest)
      if (compareTimeStrings(booking.start_time, existing.start_time) < 0) {
        existing.start_time = booking.start_time;
      }
      
      // Compare and update end_time (keep latest)
      if (compareTimeStrings(booking.end_time, existing.end_time) > 0) {
        existing.end_time = booking.end_time;
      }
      
      // Sum amounts (only non-zero amounts)
      const existingAmount = existing.amount || 0;
      const newAmount = booking.amount || 0;
      existing.amount = existingAmount + newAmount;
      
      // Sum advance payments
      const existingAdvance = existing.advance_payment || 0;
      const newAdvance = booking.advance_payment || 0;
      existing.advance_payment = existingAdvance + newAdvance;
      
      // Track if any booking has a valid amount
      if (newAmount > 0) {
        existing.has_valid_amount = true;
      }
      
      // Keep track of original bookings
      existing.original_bookings.push(booking);
      existing.merged_count += 1;
      
      // Combine booking IDs
      if (!existing.merged_booking_ids) {
        existing.merged_booking_ids = [existing.booking_id];
      }
      existing.merged_booking_ids.push(booking.booking_id);
    }
  });

  // Add duplicate bookings back (marked) if not excluding
  const mergedResults = Array.from(groupedMap.values());
  
  // Also return duplicates separately marked
  const duplicates = bookingsWithDuplicateFlag.filter(b => b.is_duplicate);
  
  return [...mergedResults, ...duplicates];
};

/**
 * Compare two time strings in "HH:MM AM/PM" format
 * Returns: -1 if time1 < time2, 0 if equal, 1 if time1 > time2
 */
export const compareTimeStrings = (time1, time2) => {
  if (!time1 || !time2) return 0;
  
  const parseTime = (timeStr) => {
    // Handle formats like "09:00 AM", "9:00 AM", "09:00AM"
    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return 0;
    
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    
    // Convert to 24-hour format for comparison
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }
    
    return hours * 60 + minutes;
  };
  
  const t1 = parseTime(time1);
  const t2 = parseTime(time2);
  
  if (t1 < t2) return -1;
  if (t1 > t2) return 1;
  return 0;
};

/**
 * Format merged booking display
 */
export const formatMergedBooking = (booking) => {
  if (booking.merged_count > 1) {
    return {
      ...booking,
      display_time: `${booking.start_time} - ${booking.end_time}`,
      display_slots: `${booking.merged_count} slots`,
    };
  }
  return {
    ...booking,
    display_time: `${booking.start_time} - ${booking.end_time}`,
    display_slots: '1 slot',
  };
};

/**
 * Format amount for display
 * Shows "Pending" for 0 or null amounts
 */
export const formatAmount = (amount) => {
  if (amount === null || amount === undefined || amount === 0) {
    return 'Pending';
  }
  return `₹${amount.toLocaleString('en-IN')}`;
};

/**
 * Check if amount should show as pending
 */
export const isAmountPending = (amount) => {
  return amount === null || amount === undefined || amount === 0;
};

export default {
  mergeConsecutiveBookings,
  compareTimeStrings,
  formatMergedBooking,
  detectDuplicates,
  filterOutDuplicates,
  formatAmount,
  isAmountPending,
};
